import { ArrowRightOutlined } from '@ant-design/icons';
import { Avatar, Button, Flex, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import NoImage from 'assets/no-image.png';
import Table from 'components/Table';
import Tag from 'components/Tags';
import { useTranslation } from 'react-i18next';
import { getWhatsappStatus } from 'screens/stakeholders/helper';
import { PaddyBuyer } from 'types/common';
import {
  isMobileScreenSize,
  renderDate,
  validateImageURL,
} from 'utils/helpers';
import StakeholderStatus from '../StakeholderStatus';

type TablePropsPB = TableProps<PaddyBuyer>;

type PaddyBuyerTableProps = {
  data?: PaddyBuyer[];
  isLoading: boolean;
  totalData: number;
  currentPage: number;
  pageSize: number;
  sortedInfo: SorterResult<PaddyBuyer>;
  selectedRow?: PaddyBuyer;
  handlePageChange: (page: number, pageSize: number) => void;
  handleChange: TablePropsPB['onChange'];
  handleAction: (data: PaddyBuyer) => void;
};

const PaddyBuyerTable = ({
  data,
  totalData,
  isLoading,
  currentPage,
  pageSize,
  sortedInfo,
  selectedRow,
  handlePageChange,
  handleChange,
  handleAction,
}: PaddyBuyerTableProps) => {
  const { t } = useTranslation();
  const paddyBuyerTableHeader: TablePropsPB['columns'] = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      onCell: (data) => ({ onClick: () => handleAction(data) }),
      className: 'clickable',
      render: (name, data) => (
        <Flex align="center" justify="space-between" gap={4}>
          <Flex align="center" gap={8}>
            <Avatar
              src={
                validateImageURL(data?.documentList?.[0]?.url)
                  ? data.documentList[0].url
                  : NoImage
              }
              style={{ minWidth: '32px' }}
            />
            <div className="first-column-text">{name}</div>
          </Flex>
          {!isMobileScreenSize ? (
            <Button className="table-click-indicator" type="text">
              <ArrowRightOutlined />
            </Button>
          ) : null}
        </Flex>
      ),
      fixed: 'left',
      width: isMobileScreenSize ? 100 : 180,
    },
    {
      title: t('common.filters.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <StakeholderStatus status={status} />,
    },
    {
      title: t('common.filters.farmerGroup'),
      dataIndex: 'farmerGroupCount',
      key: 'farmerGroupCount',
      width: 150,
    },
    {
      title: t('farmerDetailsPage.phoneNumber'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
    },
    {
      title: t('stakeholders.waVerification'),
      dataIndex: 'waVerifiedStatus',
      key: 'waVerifiedStatus',
      render: (status) => {
        const waStatus = getWhatsappStatus(status);
        return <Tag label={waStatus.label} type={waStatus.statusColor} />;
      },
      width: 150,
    },
    {
      title: t('addFgForm.subDistrict'),
      dataIndex: ['address', 'subDistrict'],
      key: 'subDistrict',
      sorter: true,
      sortOrder:
        sortedInfo.columnKey === 'subDistrict' ? sortedInfo.order : null,
      width: 150,
    },
    {
      title: t('stakeholders.seniorAgronomistEmail'),
      dataIndex: ['agronomist', 'email'],
      key: 'srAgronomist',
      width: 150,
    },
    {
      title: t('common.createdDate'),
      dataIndex: 'reportedDate',
      key: 'reportedDate',
      render: (createdDate) => renderDate(createdDate),
      width: 120,
    },
  ];

  return (
    <>
      <Table
        tableColumns={data}
        tableHeader={paddyBuyerTableHeader}
        loading={isLoading}
        onChange={handleChange}
        highlighted={selectedRow?.id}
        highlightColor="gray"
        pagination={{
          total: totalData,
          current: currentPage,
          pageSize: pageSize,
          onChange: handlePageChange,
        }}
      />
    </>
  );
};

export default PaddyBuyerTable;
